import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Phone,
  WhatsApp,
  Facebook,
  Instagram,
  Twitter,
  Send,
  ExpandMore,
} from '@mui/icons-material';
import emailjs from 'emailjs-com';
import { useTranslation } from '../hooks/useTranslation';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [errors, setErrors] = useState({});

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Allow only digits, spaces, hyphens, parentheses, and plus sign
    const phoneRegex = /^[+]?[0-9\s-()]{10,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number (numbers only)';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, allow only numeric characters, spaces, hyphens, parentheses, and plus
    if (name === 'phone') {
      const sanitizedValue = value.replace(/[^0-9\s\-()+ ]/g, '');
      setFormData(prev => ({
        ...prev,
        [name]: sanitizedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setAlert({
        show: true,
        type: 'error',
        message: 'Please fix the errors above before submitting.'
      });
      return;
    }

    setLoading(true);

    try {
      // EmailJS configuration - You'll need to set up EmailJS account
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        message: formData.message,
        to_email: 'info@acharyaabhishek.com'
      };

      // Replace these with your actual EmailJS credentials
      await emailjs.send(
        'YOUR_SERVICE_ID', // Replace with your EmailJS service ID
        'YOUR_TEMPLATE_ID', // Replace with your EmailJS template ID
        templateParams,
        'YOUR_PUBLIC_KEY' // Replace with your EmailJS public key
      );

      setAlert({
        show: true,
        type: 'success',
        message: t('contact.form.success')
      });

      // Clear form
      setFormData({ name: '', email: '', phone: '', message: '' });

    } catch (error) {
      setAlert({
        show: true,
        type: 'error',
        message: t('contact.form.error')
      });
    } finally {
      setLoading(false);
      // Hide alert after 5 seconds
      setTimeout(() => {
        setAlert({ show: false, type: '', message: '' });
      }, 5000);
    }
  };


  const socialMedia = [
    {
      icon: <Facebook />,
      name: 'Facebook',
      link: 'https://facebook.com/acharyaabhishekpandey',
      color: '#1877F2'
    },
    {
      icon: <Instagram />,
      name: 'Instagram',
      link: 'https://instagram.com/acharyaabhishekpandey',
      color: '#E4405F'
    },
    {
      icon: <Twitter />,
      name: 'Twitter',
      link: 'https://twitter.com/acharyaabhishek',
      color: '#1DA1F2'
    },
    {
      icon: <WhatsApp />,
      name: 'WhatsApp',
      link: 'https://wa.me/919838167840',
      color: '#25D366'
    }
  ];

  return (
    <Box>
      {/* Header Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #FF6B35 0%, #1976D2 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="lg">
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 6,
              flexWrap: { xs: 'wrap', lg: 'nowrap' },
              minHeight: '200px'
            }}
          >
            {/* Left Side - Title and Description */}
            <Box sx={{ 
              flex: 1, 
              textAlign: { xs: 'center', lg: 'left' },
              minWidth: { xs: '100%', lg: 'auto' }
            }}>
              <Typography
                variant="h1"
                gutterBottom
                sx={{ 
                  fontWeight: 700, 
                  mb: 3,
                  fontSize: { xs: '2.5rem', md: '3.5rem' }
                }}
              >
                {t('contact.title')}
              </Typography>
              <Typography
                variant="h5"
                sx={{ 
                  opacity: 0.9, 
                  lineHeight: 1.4,
                  fontSize: { xs: '1.1rem', md: '1.3rem' },
                  maxWidth: { xs: '100%', lg: '400px' }
                }}
              >
                {t('contact.description')}
              </Typography>
            </Box>

            {/* Vertical Divider - Hidden on mobile */}
            <Box 
              sx={{ 
                width: '2px',
                height: '150px',
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                display: { xs: 'none', lg: 'block' },
                flexShrink: 0
              }}
            />

            {/* Right Side - Quick Contact and Follow Us */}
            <Box sx={{ 
              flex: 1, 
              display: 'flex', 
              flexDirection: 'column',
              gap: 4,
              alignItems: 'center',
              minWidth: { xs: '100%', lg: 'auto' }
            }}>
              {/* Quick Contact Section */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    fontSize: '1.2rem'
                  }}
                >
                  {t('contact.quickContact')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    startIcon={<Phone />}
                    href="tel:+919838167840"
                    sx={{
                      backgroundColor: '#FF6B35',
                      color: 'white',
                      px: 3,
                      py: 1.2,
                      borderRadius: '25px',
                      fontWeight: 600,
                      minWidth: '130px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      '&:hover': {
                        backgroundColor: '#E55A2B',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    Call Now
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={<WhatsApp />}
                    href="https://wa.me/919838167840"
                    target="_blank"
                    sx={{
                      backgroundColor: '#FF6B35',
                      color: 'white',
                      px: 3,
                      py: 1.2,
                      borderRadius: '25px',
                      fontWeight: 600,
                      minWidth: '130px',
                      boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                      '&:hover': {
                        backgroundColor: '#E55A2B',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                      },
                      transition: 'all 0.3s ease',
                    }}
                  >
                    WhatsApp करें
                  </Button>
                </Box>
              </Box>

              {/* Follow Us Section */}
              <Box sx={{ textAlign: 'center' }}>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ 
                    fontWeight: 600, 
                    mb: 2,
                    fontSize: '1.2rem'
                  }}
                >
                  {t('contact.followUs')}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  gap: 2, 
                  flexWrap: 'wrap'
                }}>
                  {socialMedia.map((social, index) => (
                    <IconButton
                      key={index}
                      href={social.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        backgroundColor: social.color,
                        color: 'white',
                        width: 50,
                        height: 50,
                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                        '&:hover': {
                          backgroundColor: social.color,
                          transform: 'translateY(-2px) scale(1.05)',
                          boxShadow: '0 6px 20px rgba(0,0,0,0.3)',
                        },
                        transition: 'all 0.3s ease',
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6}>

          {/* Contact Form */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 4,
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                border: '1px solid #e9ecef',
              }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{ 
                  fontWeight: 700, 
                  color: '#333', 
                  mb: 1,
                  fontSize: '1.8rem',
                }}
              >
                {t('contact.form.title')}
              </Typography>

              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ mb: 3, lineHeight: 1.6 }}
              >
                We're here to help! Tell us how we can best serve you. Use the form below to email us or select from the topics that best fit your needs. It's an honor to help you in your journey towards better health.
              </Typography>

              {alert.show && (
                <Alert 
                  severity={alert.type} 
                  sx={{ mb: 3 }}
                  onClose={() => setAlert({ show: false, type: '', message: '' })}
                >
                  {alert.message}
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <TextField
                    fullWidth
                    placeholder={t('contact.form.name')}
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    error={!!errors.name}
                    helperText={errors.name}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: errors.name ? '#f44336' : '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: errors.name ? '#f44336' : '#b0b0b0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: errors.name ? '#f44336' : '#FF6B35',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    placeholder={t('contact.form.emailLabel')}
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: errors.email ? '#f44336' : '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: errors.email ? '#f44336' : '#b0b0b0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: errors.email ? '#f44336' : '#FF6B35',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    placeholder="Phone number (optional)"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    variant="outlined"
                    error={!!errors.phone}
                    helperText={errors.phone}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: errors.phone ? '#f44336' : '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: errors.phone ? '#f44336' : '#b0b0b0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: errors.phone ? '#f44336' : '#FF6B35',
                        },
                      },
                    }}
                  />

                  <TextField
                    fullWidth
                    placeholder="Comment"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    variant="outlined"
                    multiline
                    rows={5}
                    error={!!errors.message}
                    helperText={errors.message}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        '& fieldset': {
                          borderColor: errors.message ? '#f44336' : '#e0e0e0',
                        },
                        '&:hover fieldset': {
                          borderColor: errors.message ? '#f44336' : '#b0b0b0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: errors.message ? '#f44336' : '#FF6B35',
                        },
                      },
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={20} /> : <Send />}
                    sx={{
                      py: 2,
                      px: 4,
                      fontSize: '1rem',
                      fontWeight: 600,
                      backgroundColor: '#B8860B',
                      borderRadius: '8px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      '&:hover': {
                        backgroundColor: '#A0750A',
                      },
                      '&:disabled': {
                        backgroundColor: '#e0e0e0',
                      },
                      alignSelf: 'flex-start',
                      minWidth: '160px',
                    }}
                  >
                    {loading ? t('contact.form.sending') : 'SEND MESSAGE'}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 3 }} />
              
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center' }}
              >
                {t('contact.response24hrs')} {t('contact.emergencyCall')}
              </Typography>
            </Paper>
          </Grid>


        </Grid>
      </Container>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: 'background.default', py: 6 }}>
        <Container maxWidth="md">
          <Typography
            variant="h4"
            align="center"
            gutterBottom
            sx={{ fontWeight: 600, mb: 4 }}
          >
            {t('contact.faq.title')}
          </Typography>

          <Box sx={{ maxWidth: 800, mx: 'auto' }}>
            {[
              {
                question: t('contact.faq.consultation.question'),
                answer: t('contact.faq.consultation.answer')
              },
              {
                question: t('contact.faq.online.question'),
                answer: t('contact.faq.online.answer')
              },
              {
                question: t('contact.faq.fees.question'),
                answer: t('contact.faq.fees.answer')
              },
              {
                question: t('contact.faq.payment.question'),
                answer: t('contact.faq.payment.answer')
              }
            ].map((faq, index) => (
              <Accordion 
                key={index}
                sx={{
                  mb: 2,
                  boxShadow: 'none',
                  border: '1px solid',
                  borderColor: 'divider',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: '0 0 16px 0',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    backgroundColor: 'background.paper',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: '12px 0',
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: 'primary.main' }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: 'background.default',
                    pt: 0,
                    pb: 2,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Contact;